/**
 * @copyright Copyright 2021 Epic Systems Corporation
 * @file One application row in a dashboard widget table
 * @module Epic.AppOrchard.Dashboard.WidgetAppRow
 */

import {
	AspectRatio,
	Box,
	Center,
	Divider,
	Flex,
	GridItem,
	HStack,
	Image,
	ListItem,
	SimpleGrid,
	useBreakpointValue,
} from "@chakra-ui/react";
import { config } from "ao/appConfig";
import { AOLink } from "ao/components/Core";
import { getBaseUrl } from "ao/utils/helpers";
import { childrenWithoutFragment } from "ao/utils/reactHelpers";
import React, { FC, memo, useEffect, useMemo, useRef } from "react";

export interface IWidgetAppRowProps {
	appId: number;
	/** actions, e.g. buttons or inputs */
	actions?: React.ReactNode;
	/** Content that shows underneath the app row */
	bottomContent?: React.ReactNode;
	/** content to show in the middle column of the app row,
	 * in between the standard app info (name, logo, etc) and content on the right  */
	middleColumnContent?: React.ReactNode;
	/** additional info about the app shown under app name or to the side if no actions passed */
	additionalAppInfo?: React.ReactNode;
	appName: string;
	/** page to link to when app is clicked  */
	linkUrl?: string;
	linkTitle?: string;
	/** show under app name */
	subtitle?: React.ReactNode;
	belowLogoContent?: React.ReactNode;
	/** get a reference to the app name link, can be used to focus something in the app after an action is taken */
	onLinkRefCreated?: (linkRef: React.RefObject<HTMLAnchorElement> | null) => void;
}

/** One application row in a dashboard widget table.
 *   Some of the content is shown in different places depending on the screen size and which content is passed to try to prevent empty sections in the app row */
export const WidgetAppRow: FC<IWidgetAppRowProps> = memo((props: IWidgetAppRowProps) => {
	const {
		appId,
		appName,
		subtitle,
		actions,
		linkUrl,
		bottomContent,
		linkTitle,
		additionalAppInfo,
		middleColumnContent,
		belowLogoContent,
		onLinkRefCreated,
	} = props;
	const baseUrl = useMemo(() => getBaseUrl(), []);
	const thumbnailUrl = `${baseUrl}Gallery/Thumbnail?appId=${appId}`;
	const fallbackThumbnailUrl = `${baseUrl}Content/images/app_placeholder.png`;
	const thumbnailColor = "appThumbnailBackground." + (appId % config.NumAppThumbnailColors);
	const isMobile = useBreakpointValue({ base: true, lg: false });
	const linkRef = useRef<HTMLAnchorElement>(null);

	useEffect(() => onLinkRefCreated && onLinkRefCreated(linkRef), [onLinkRefCreated]);

	return (
		<ListItem>
			<SimpleGrid
				columns={12}
				p="0.5em"
				borderRadius="4px"
				backgroundColor="white"
				m="6px 0"
				border="1px solid lightgrey"
			>
				<GridItem colSpan={{ base: 3, lg: 1 }}>
					{/* App logo */}
					<Center>
						<AspectRatio maxW="55px" ratio={1} flex="1 1 auto">
							<Image
								src={thumbnailUrl}
								fallbackSrc={fallbackThumbnailUrl}
								alt="App logo"
								background={thumbnailColor}
							/>
						</AspectRatio>
					</Center>
					{childrenWithoutFragment(belowLogoContent) && (
						<Center>
							<HStack spacing="0.5em" mt="0.25em">
								{belowLogoContent}
							</HStack>
						</Center>
					)}
				</GridItem>
				<GridItem colSpan={{ base: 9, lg: 4 }} ml="0.75em" mr="0.5em">
					{/* App name, org name, and potentially actions or other app info depending on screen size and what's passed  */}

					{linkUrl ? (
						<AOLink
							ref={linkRef}
							variant="black"
							url={linkUrl}
							title={linkTitle}
							target="_blank"
							fontWeight="bold"
						>
							{appName}
						</AOLink>
					) : (
						<Box fontWeight="bold">{appName}</Box>
					)}
					{subtitle && <Box>{subtitle}</Box>}
					{actions || isMobile ? (
						<Box mt="0.6em" textAlign="left">
							{additionalAppInfo}
						</Box>
					) : null}
				</GridItem>
				<GridItem
					colSpan={{ base: 12, lg: 3 }}
					ml={{ base: 0, lg: "0.75em" }}
					mt={{ base: "0.5em", lg: 0 }}
				>
					<Flex justify="flex-start"> {middleColumnContent}</Flex>
				</GridItem>
				<GridItem colSpan={{ base: 12, lg: 4 }} mt={{ base: "0.5em", lg: 0 }}>
					{/* Actions or other app info depending on screen size and what's passed  */}
					{actions ? (
						<Flex justify={{ base: "flex-start", lg: "flex-end" }} mr="0.5em">
							{actions}
						</Flex>
					) : !isMobile ? (
						<Flex justify="flex-end" mr="0.5em" textAlign="right">
							{additionalAppInfo}
						</Flex>
					) : null}
				</GridItem>
				{childrenWithoutFragment(bottomContent) && (
					<GridItem colSpan={12} mt={{ base: "0.5em", lg: 0 }}>
						<Divider m="0.5em 0" />
						<HStack spacing="1em">{bottomContent}</HStack>
					</GridItem>
				)}
			</SimpleGrid>
		</ListItem>
	);
});
